import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import { TaskStatus } from '@/store/Employee/DailyPlanStore';
import { DayStatus } from '@/store/hr/PlanBoardStore';

type ChipColor = ChipProps['color'];

/**
 * Task status. Note `carried` and `dropped` both mean "was NOT completed on its
 * plan_date" — they are the downstream resolutions of a not_completed task, which
 * is why they are coloured as failures rather than as neutral outcomes.
 */
const TASK_LABEL: Record<TaskStatus, { label: string; color: ChipColor }> = {
  completed: { label: 'Completed', color: 'success' },
  not_completed: { label: 'Not completed', color: 'error' },
  carried: { label: 'Carried forward', color: 'warning' },
  dropped: { label: 'Dropped', color: 'default' },
  planned: { label: 'Open', color: 'info' }
};

export const TaskStatusChip: React.FC<{ status: TaskStatus; size?: 'small' | 'medium' }> = ({
  status,
  size = 'small'
}) => {
  const spec = TASK_LABEL[status] || { label: status, color: 'default' as ChipColor };
  return <Chip label={spec.label} color={spec.color} size={size} sx={{ fontWeight: 600 }} />;
};

const DAY_LABEL: Record<DayStatus, { label: string; color: ChipColor }> = {
  eod_done: { label: 'Day closed', color: 'success' },
  in_progress: { label: 'Working', color: 'info' },
  forgot_eod: { label: 'No EOD', color: 'warning' },
  no_plan: { label: 'No plan', color: 'error' },
  punched_no_plan: { label: 'Punched, no plan', color: 'warning' },
  on_leave: { label: 'On leave', color: 'default' },
  holiday: { label: 'Holiday', color: 'default' },
  weekend: { label: 'Non-working', color: 'default' }
};

export const DayStatusChip: React.FC<{ status: DayStatus; size?: 'small' | 'medium' }> = ({
  status,
  size = 'small'
}) => {
  const spec = DAY_LABEL[status] || { label: status, color: 'default' as ChipColor };
  return <Chip label={spec.label} color={spec.color} size={size} sx={{ fontWeight: 600 }} />;
};

/** Completion rate is null — not 0 — when nothing is resolved yet. */
export const formatRate = (rate: number | null | undefined): string =>
  rate === null || rate === undefined ? '—' : `${Math.round(rate * 100)}%`;

export default TaskStatusChip;
